import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  useMediaQuery,
} from "@mui/material";
import {
  displayTransactionStatus,
  hasFinalInvoiceStatus,
  isWrittenOffWeek,
} from "../../../routes/pages/org.utils";
import type { WeeklyPeriod, WeeklyCollection } from "../../../routes/pages/org.utils";
import type {
  WeeklyBilling,
  WeeklyStatementPage,
  WeeklyStatementSummary,
} from "../../../routes/pages/org.weekly";
import { WeeklyPixSummary } from "./WeeklyPixSummary";
import { WeeklyGroupedStatement } from "./WeeklyGroupedStatement";
import { NEGOTIATION_TOPUP_LABEL } from "./weeklyStatementLabels";
import { formatBRL } from "../InvoiceSplitPreviewModal";
import "./WeeklyStatementDialog.css";

export type WeeklyStatementDialogProps = {
  open: boolean;
  summary?: WeeklyStatementSummary;
  period?: WeeklyPeriod;
  invoiceStatus: string;
  collection?: WeeklyCollection;
  billing?: WeeklyBilling;
  onOpenTarget?: (invoiceId: string) => void;
  page?: WeeklyStatementPage;
  pageIndex?: number;
  onClose: () => void;
  onRefresh?: () => void;
  onPageChange?: (page: number) => void;
  refreshing?: boolean;
  refreshError?: boolean;
};

/**
 * The weekly parent's read-only, paginated statement. Calendar dates and
 * source totals never imply an unobserved bank debit.
 * @story args { open: true, summary: { serviceAmountCents: 1014, totalAmountCents: 1014, groupCount: 2 }, period: { startsOn: "2026-10-05", endsOn: "2026-10-12", dueOn: "2026-10-14" }, invoiceStatus: "weekly-open", page: { invoiceId: "weekly", invoiceStatus: "weekly-open", totalPages: 1, summary: { serviceAmountCents: 1014, totalAmountCents: 1014, groupCount: 2 }, groups: [{ groupId: "hall", lineClass: "service", kind: "hall", amountCents: 1000, negotiationInvoiceId: "negotiation", weeklyLimit: { mode: "unlimited" }, weeklyLimitVersion: 1 }, { groupId: "base", lineClass: "service", kind: "base-hall", amountCents: 14, monthlyBaseCents: 220, proration: "monthly-cumulative-floor-v1", serviceDates: ["2026-10-05", "2026-10-06"], dailyAmounts: [{ serviceDate: "2026-10-05", amountCents: 7 }, { serviceDate: "2026-10-06", amountCents: 7 }], weeklyLimit: { mode: "unlimited" }, weeklyLimitVersion: 1 }] } }
 * @story state Unconfirmed { invoiceStatus: "weekly-closed", collection: { phase: "pending", commandState: "unknown" } }
 * @story state RefreshFailed { refreshError: true }
 * @story state Loading { page: undefined, refreshing: true }
 * @story state Deferred { invoiceStatus: "weekly-deferred", collection: undefined, page: undefined, summary: { serviceAmountCents: 99, totalAmountCents: 99, groupCount: 1 }, billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "deferred", disposition: "accumulation", ownServiceAmountCents: 99, carriedServiceAmountCents: 0, netServiceAmountCents: 99 } }
 * @story state AccumulatedPreview { invoiceStatus: "weekly-open", collection: undefined, period: { startsOn: "2026-10-12", endsOn: "2026-10-19", dueOn: "2026-10-21" }, summary: { serviceAmountCents: 110, totalAmountCents: 110, groupCount: 2 }, billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "open", disposition: "accumulation", ownServiceAmountCents: 11, carriedServiceAmountCents: 99, eligibleCustomerCreditAmountCents: -10, netServiceAmountCents: 100 }, page: { invoiceId: "target", invoiceStatus: "weekly-open", totalPages: 1, weeklyBilling: { policyVersion: 2, minimumNetServicesCents: 100, state: "open", disposition: "accumulation", ownServiceAmountCents: 11, carriedServiceAmountCents: 99, eligibleCustomerCreditAmountCents: -10, netServiceAmountCents: 100 }, summary: { serviceAmountCents: 110, totalAmountCents: 110, groupCount: 2 }, groups: [{ groupId: "origin", lineClass: "service", kind: "radio", amountCents: 99, originInvoiceId: "origin", originPeriod: { startsOn: "2026-10-05", endsOn: "2026-10-12" }, weeklyLimit: { mode: "unlimited" }, weeklyLimitVersion: 1 }, { groupId: "own", lineClass: "service", kind: "radio", amountCents: 11, originInvoiceId: "target", originPeriod: { startsOn: "2026-10-12", endsOn: "2026-10-19" }, weeklyLimit: { mode: "unlimited" }, weeklyLimitVersion: 1 }] } }
 * @story state Contribution { invoiceStatus: "weekly-carried", collection: undefined, page: undefined, summary: { serviceAmountCents: 99, totalAmountCents: 99, groupCount: 1 }, billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "carried", disposition: "contribution", ownServiceAmountCents: 99, carriedServiceAmountCents: 0, targetInvoiceId: "target" } }
 * @story state WrittenOff { invoiceStatus: "weekly-written-off", collection: { phase: "stopped", commandState: "blocked" }, page: undefined, summary: { serviceAmountCents: 1021, processingFeeAmountCents: 350, totalAmountCents: 1371, groupCount: 3 }, billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "written_off", disposition: "accumulation", ownServiceAmountCents: 1021, carriedServiceAmountCents: 0, netServiceAmountCents: 1021 } }
 */
export function WeeklyStatementDialog({
  open,
  onClose,
  onRefresh,
  onPageChange,
  refreshing = false,
  refreshError = false,
  summary,
  period,
  invoiceStatus,
  collection,
  billing,
  onOpenTarget,
  page,
  pageIndex = 0,
}: WeeklyStatementDialogProps) {
  const mobile = useMediaQuery("(max-width: 767px)");
  const titleId = React.useId();
  if (!open) return null;
  const writtenOff = isWrittenOffWeek({ invoiceStatus, weeklyBilling: billing });
  const final = writtenOff || hasFinalInvoiceStatus(invoiceStatus);
  const pending =
    !final &&
    collection &&
    (collection.phase === "pending" || collection.commandState === "unknown");
  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={mobile}
      fullWidth
      maxWidth="sm"
      aria-labelledby={titleId}
      className="weeklyStatementSheet"
      PaperProps={{ className: "weeklyStatementDialog" }}
    >
      <DialogTitle id={titleId} className="title">
        Extrato semanal
      </DialogTitle>
      <DialogContent className="body" data-testid="weekly-statement-dialog">
        {invoiceStatus && (
          <div className="state" data-testid="weekly-statement-status">
            {displayTransactionStatus({
              invoiceStatus,
              weeklyCollection: collection,
              weeklyBilling: billing,
            })}
          </div>
        )}
        {summary && (
          <WeeklyPixSummary
            statement={summary}
            period={period}
            invoiceStatus={invoiceStatus}
            billing={billing}
          />
        )}
        {writtenOff && (
          <p className="notice" data-testid="weekly-written-off-notice">
            Esta cobrança foi perdoada e não será cobrada.
          </p>
        )}
        {period && !final && (!billing || billing.expectedChargeOn) && (
          <p className="notice">A data prevista não confirma agendamento nem débito no banco.</p>
        )}
        {invoiceStatus === "weekly-open" && (
          <p className="notice">Os valores podem mudar até o fechamento da semana.</p>
        )}
        {pending && (
          <p className="notice">
            Aguardando confirmação da cobrança. Nenhum débito confirmado nesta visualização.
          </p>
        )}
        {billing && (!final || billing.disposition === "contribution") && (
          <div className="notice" data-testid="weekly-billing-policy">
            {billing.disposition === "contribution" ? (
              <>
                <p>
                  Estes serviços fazem parte de outro extrato acumulado. Este valor identifica a
                  semana de origem e não é uma cobrança separada.
                </p>
                {onOpenTarget && billing.targetInvoiceId && (
                  <Button onClick={() => onOpenTarget(billing.targetInvoiceId!)}>
                    Ver extrato acumulado
                  </Button>
                )}
              </>
            ) : (
              <>
                {billing.state === "deferred" && (
                  <p>
                    Aguardando acúmulo: os serviços líquidos após créditos elegíveis ainda não
                    atingiram o mínimo de {formatBRL(billing.minimumNetServicesCents)}, antes da
                    tarifa. Os custos ficam para uma próxima semana.
                  </p>
                )}
                {billing.state === "awaiting_preparation" && (
                  <p>
                    Os serviços aguardam a preparação da cobrança. Nenhuma tarifa foi adicionada a
                    este extrato ainda.
                  </p>
                )}
                {billing.state === "held" && (
                  <p>
                    {billing.holdReason === "missed_window"
                      ? "A data de preparação passou. Os custos aguardam acúmulo em uma próxima semana."
                      : billing.holdReason === "limit_below_minimum" ||
                          billing.holdReason === "limit_exceeded"
                        ? "A cobrança aguarda um limite que comporte os serviços acumulados e a tarifa."
                        : billing.holdReason === "credit_unresolved"
                          ? "A cobrança aguarda a conferência dos créditos elegíveis."
                          : "A preparação da cobrança tem uma pendência. Nenhum débito é confirmado por este estado."}
                  </p>
                )}
                {summary?.processingFeeAmountCents === undefined && (
                  <p>
                    A tarifa Pix só entra quando a cobrança é preparada para envio. O mínimo é de
                    serviços líquidos, antes dessa tarifa.
                  </p>
                )}
              </>
            )}
          </div>
        )}
        {refreshing && <p role="status">Atualizando extrato...</p>}
        {refreshError && (
          <Alert severity="error">
            Não foi possível carregar o extrato. O resumo e os valores exibidos correspondem à
            última leitura.
          </Alert>
        )}
        {page ? (
          <WeeklyGroupedStatement page={page} />
        ) : (
          <dl className="summaryCharges">
            {summary?.processingFeeAmountCents !== undefined && (
              <div>
                <dt>Tarifa fixa Pix</dt>
                <dd>{formatBRL(summary.processingFeeAmountCents)}</dd>
              </div>
            )}
            {summary?.customerCreditAmountCents !== undefined && (
              <div>
                <dt>Crédito da empresa</dt>
                <dd>{formatBRL(summary.customerCreditAmountCents)}</dd>
              </div>
            )}
            {summary?.negotiationTopupAmountCents !== undefined && (
              <div>
                <dt>{NEGOTIATION_TOPUP_LABEL}</dt>
                <dd>{formatBRL(summary.negotiationTopupAmountCents)}</dd>
              </div>
            )}
          </dl>
        )}
        {page && page.totalPages > 1 && onPageChange && (
          <Pagination
            aria-label="Páginas do extrato semanal"
            count={page.totalPages}
            page={pageIndex + 1}
            disabled={refreshing}
            onChange={(_, next) => onPageChange(next - 1)}
          />
        )}
      </DialogContent>
      <DialogActions className="actions">
        {onRefresh && (
          <Button onClick={onRefresh} disabled={refreshing}>
            Atualizar extrato
          </Button>
        )}
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
